import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  FileText,
  Camera,
  AlertTriangle,
  Check,
  Stethoscope,
  Play,
  CheckCircle2,
  Plus,
  Download,
  Grid3X3,
  Share2,
  MapPin,
  CalendarPlus,
  CreditCard,
} from "lucide-react";
import AddNoteModal from "../components/aesthetics/treatments/AddNoteModal";
import AddPhotosModal from "../components/aesthetics/clients/AddPhotosModal";
import AddDocumentModal from "../components/aesthetics/clients/AddDocumentModal";
import TakePaymentModal from "../components/aesthetics/treatments/TakePaymentModal";
import RebookTreatmentModal from "../components/aesthetics/treatments/RebookTreatmentModal";
import ScheduleStartModal from "../components/aesthetics/treatments/ScheduleStartModal";
import ShareDocumentModal from "../components/aesthetics/treatments/ShareDocumentModal";
import DocumentPreviewModal from "../components/aesthetics/treatments/DocumentPreviewModal";

// Types
type TreatmentStatus = "Upcoming" | "In Progress" | "Completed";
type ConsentStatus = "Pending" | "Signed" | "Not Required";
type AfterCareStatus = "Shared" | "Not shared";
type TabType = "overview" | "notes" | "photos" | "documents" | "log";
type PhotoFilterType = "before" | "after";
type DocumentStatus = "Signed" | "Completed" | "Draft";

interface Note {
  id: string;
  content: string;
  author: string;
  date: string;
  avatarBg: string;
}

interface Photo {
  id: string;
  type: "before" | "after";
  url: string;
  date: string;
  practitioner: string;
  area?: string;
}

interface Document {
  id: string;
  name: string;
  category: string;
  status: DocumentStatus;
  dateUploaded: string;
  url?: string;
  signedBy?: string;
  signedDate?: string;
  pages?: number;
}

interface LogEntry {
  id: string;
  action: string;
  user: string;
  time: string;
  type: "system" | "user" | "payment" | "document" | "status";
}

interface TreatmentDetail {
  id: string;
  client: {
    name: string;
    initials: string;
    avatarBg: string;
    email?: string;
    phone?: string;
  };
  treatment: string;
  treatmentDescription?: string;
  dateTime: string;
  practitioner: string;
  status: TreatmentStatus;
  price: number;
  paidAmount: number;
  consent: ConsentStatus;
  aftercare: AfterCareStatus;
  clinicalNotes: string;
  aftercareNotes: string;
  shareWithPatient: boolean;
  additionalNotes: Note[];
  photos: Photo[];
  documents: Document[];
  logs: LogEntry[];
  treatmentArea?: string;
  duration?: string;
  nextAppointment?: string;
}

// Static treatment data for demo
const staticTreatmentData: Record<string, TreatmentDetail> = {
  "1": {
    id: "1",
    client: {
      name: "Sarah Miller",
      initials: "SM",
      avatarBg: "bg-purple-100 text-purple-600",
      email: "sarah.miller@email.com",
      phone: "+44 7700 900123",
    },
    treatment: "Hydrafacial",
    treatmentDescription: "Deep cleansing facial treatment with hydradermabrasion technology",
    dateTime: "May 25, 2024 at 10:00 AM",
    practitioner: "Emma Taylor",
    status: "Upcoming",
    price: 180.0,
    paidAmount: 50.0,
    consent: "Pending",
    aftercare: "Not shared",
    treatmentArea: "Full Face",
    duration: "60 minutes",
    clinicalNotes: "",
    aftercareNotes: "",
    shareWithPatient: false,
    additionalNotes: [
      {
        id: "1",
        content: "Client has sensitive skin around the eye area - use gentle pressure and avoid harsh extractions near the orbital bone.",
        author: "Emma Taylor",
        date: "May 20, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "2",
        content: "Previous treatment completed successfully. Client reported improved skin texture after 2 weeks.",
        author: "Dr Saleema",
        date: "April 15, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "3",
        content: "Medical history reviewed - no contraindications noted. Client is not using any retinoids currently.",
        author: "Emma Taylor",
        date: "May 18, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Before-49e05053-44482786-1920w.jpg", date: "May 20, 2024", practitioner: "Emma Taylor" }, // Forehead before
      { id: "2", type: "before", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Before+0.5ml+Lip+Enhancement-640w.jpg", date: "May 20, 2024", practitioner: "Emma Taylor" }, // Lips before
      { id: "3", type: "after", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/After-d3e8c309-58a90bf2-1920w.jpg", date: "May 25, 2024", practitioner: "Emma Taylor" }, // Forehead after
      { id: "4", type: "after", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/After+0.5ml+Lip+Enhancement-640w.jpg", date: "May 25, 2024", practitioner: "Emma Taylor" }, // Lips after
    ],
    documents: [
      { id: "1", name: "Hydrafacial Consent Form", category: "Consent", status: "Signed", dateUploaded: "May 18, 2024", signedBy: "Sarah Miller", signedDate: "May 18, 2024", pages: 2 },
      { id: "2", name: "Medical History Questionnaire", category: "Medical History", status: "Completed", dateUploaded: "May 18, 2024", signedBy: "Sarah Miller", signedDate: "May 18, 2024", pages: 4 },
      { id: "3", name: "Aftercare Instructions - Hydrafacial", category: "Aftercare", status: "Draft", dateUploaded: "May 20, 2024", pages: 1 },
      { id: "4", name: "Treatment Plan Overview", category: "Treatment Plan", status: "Completed", dateUploaded: "May 15, 2024", pages: 2 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 15, 2024 at 9:30 AM", type: "system" },
      { id: "2", action: "Consent form sent to client via email", user: "Emma Taylor", time: "May 16, 2024 at 10:15 AM", type: "document" },
      { id: "3", action: "Client signed consent form", user: "Sarah Miller", time: "May 18, 2024 at 2:30 PM", type: "document" },
      { id: "4", action: "Deposit payment received - £50.00", user: "Reception", time: "May 18, 2024 at 3:00 PM", type: "payment" },
      { id: "5", action: "Medical history form completed", user: "Sarah Miller", time: "May 18, 2024 at 3:15 PM", type: "document" },
      { id: "6", action: "Pre-treatment photos uploaded", user: "Emma Taylor", time: "May 20, 2024 at 11:00 AM", type: "user" },
      { id: "7", action: "Clinical notes added", user: "Emma Taylor", time: "May 20, 2024 at 11:30 AM", type: "user" },
      { id: "8", action: "Appointment reminder sent", user: "System", time: "May 24, 2024 at 9:00 AM", type: "system" },
    ],
  },
  "2": {
    id: "2",
    client: {
      name: "James Lee",
      initials: "JL",
      avatarBg: "bg-amber-100 text-amber-600",
      email: "james.lee@email.com",
      phone: "+44 7700 900456",
    },
    treatment: "Laser Hair Removal",
    treatmentDescription: "Permanent hair reduction using advanced diode laser technology",
    dateTime: "May 25, 2024 at 11:00 AM",
    practitioner: "Dr Saleema",
    status: "Upcoming",
    price: 350.0,
    paidAmount: 100.0,
    consent: "Pending",
    aftercare: "Not shared",
    treatmentArea: "Full Back",
    duration: "45 minutes",
    clinicalNotes: "",
    aftercareNotes: "",
    shareWithPatient: false,
    additionalNotes: [
      {
        id: "1",
        content: "Session 3 of 6. Previous sessions showed good results with approximately 40% reduction.",
        author: "Dr Saleema",
        date: "May 20, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "2",
        content: "Client advised to avoid sun exposure for 2 weeks prior to treatment. Confirmed compliance.",
        author: "Dr Saleema",
        date: "May 22, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Before-e6ff5540-13bf0a32-1920w.jpg", date: "April 10, 2024", practitioner: "Dr Saleema" }, // Wrinkles before
      { id: "2", type: "before", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Under+Eye+Treatment+Before-1920w.jpg", date: "April 10, 2024", practitioner: "Dr Saleema" }, // Eye hollowing
      { id: "3", type: "after", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/After-e1fd73fe-1034426d-1920w.jpg", date: "April 25, 2024", practitioner: "Dr Saleema" }, // Smooth face
      { id: "4", type: "after", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Under+Eye+Treatment+After-1920w.jpg", date: "April 25, 2024", practitioner: "Dr Saleema" }, // Refreshed eyes
    ],
    documents: [
      { id: "1", name: "Laser Treatment Consent", category: "Consent", status: "Draft", dateUploaded: "May 20, 2024", pages: 3 },
      { id: "2", name: "Patch Test Results", category: "Medical", status: "Completed", dateUploaded: "March 15, 2024", signedBy: "Dr Saleema", signedDate: "March 15, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 10, 2024 at 2:00 PM", type: "system" },
      { id: "2", action: "Booking confirmed for session 3", user: "Reception", time: "May 10, 2024 at 2:15 PM", type: "user" },
      { id: "3", action: "Deposit payment received - £100.00", user: "Reception", time: "May 12, 2024 at 10:30 AM", type: "payment" },
      { id: "4", action: "Pre-treatment instructions sent", user: "System", time: "May 18, 2024 at 9:00 AM", type: "system" },
    ],
  },
  "3": {
    id: "3",
    client: {
      name: "Laura Brown",
      initials: "LB",
      avatarBg: "bg-pink-100 text-pink-600",
      email: "laura.brown@email.com",
      phone: "+44 7700 900789",
    },
    treatment: "Chemical Peel",
    treatmentDescription: "Medium-depth TCA peel for skin rejuvenation and texture improvement",
    dateTime: "May 26, 2024 at 9:30 AM",
    practitioner: "Dr Saleema",
    status: "Upcoming",
    price: 220.0,
    paidAmount: 220.0,
    consent: "Signed",
    aftercare: "Not shared",
    treatmentArea: "Full Face",
    duration: "30 minutes",
    clinicalNotes: "",
    aftercareNotes: "",
    shareWithPatient: false,
    additionalNotes: [
      {
        id: "1",
        content: "Client has Fitzpatrick skin type III. Recommend 25% TCA concentration for optimal results.",
        author: "Dr Saleema",
        date: "May 22, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "2",
        content: "Pre-treatment skincare regimen completed (retinoid prep for 4 weeks). Skin is ready for peel.",
        author: "Emma Taylor",
        date: "May 24, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "3",
        content: "Client goals: reduce hyperpigmentation and improve overall skin texture. Realistic expectations discussed.",
        author: "Dr Saleema",
        date: "May 20, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Naso+Labial+Before-1920w.jpg", date: "May 22, 2024", practitioner: "Dr Saleema" }, // Naso folds
      { id: "2", type: "before", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Eight-Point-Before-6e5526e5-f0ef9b86-1920w.jpg", date: "May 22, 2024", practitioner: "Dr Saleema" }, // Face laxity
      { id: "3", type: "after", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Naso-Labial-After-988d22cb-1920w.jpg", date: "May 26, 2024", practitioner: "Dr Saleema" }, // Folds reduced
      { id: "4", type: "after", url: "https://lirp.cdn-website.com/54fa23f6/dms3rep/multi/opt/Eight-Point-After-1920w.jpg", date: "May 26, 2024", practitioner: "Dr Saleema" }, // Lifted face
    ],
    documents: [
      { id: "1", name: "Chemical Peel Consent Form", category: "Consent", status: "Signed", dateUploaded: "May 20, 2024", signedBy: "Laura Brown", signedDate: "May 21, 2024", pages: 3 },
      { id: "2", name: "Pre-Treatment Questionnaire", category: "Medical History", status: "Completed", dateUploaded: "May 18, 2024", signedBy: "Laura Brown", signedDate: "May 18, 2024", pages: 2 },
      { id: "3", name: "Aftercare Protocol - Chemical Peel", category: "Aftercare", status: "Draft", dateUploaded: "May 24, 2024", pages: 2 },
      { id: "4", name: "Skin Analysis Report", category: "Assessment", status: "Completed", dateUploaded: "May 22, 2024", signedBy: "Dr Saleema", signedDate: "May 22, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 15, 2024 at 3:00 PM", type: "system" },
      { id: "2", action: "Full payment received - £220.00", user: "Reception", time: "May 16, 2024 at 11:00 AM", type: "payment" },
      { id: "3", action: "Consent form sent to client", user: "Emma Taylor", time: "May 18, 2024 at 9:30 AM", type: "document" },
      { id: "4", action: "Pre-treatment questionnaire completed", user: "Laura Brown", time: "May 18, 2024 at 4:00 PM", type: "document" },
      { id: "5", action: "Consent form signed", user: "Laura Brown", time: "May 21, 2024 at 10:00 AM", type: "document" },
      { id: "6", action: "Skin analysis completed", user: "Dr Saleema", time: "May 22, 2024 at 2:00 PM", type: "user" },
      { id: "7", action: "Pre-treatment photos uploaded", user: "Dr Saleema", time: "May 22, 2024 at 2:30 PM", type: "user" },
    ],
  },
  "4": {
    id: "4",
    client: {
      name: "Emma Clark",
      initials: "EC",
      avatarBg: "bg-blue-100 text-blue-600",
      email: "emma.clark@email.com",
      phone: "+44 7700 901234",
    },
    treatment: "PRP Facial Rejuvenation",
    treatmentDescription: "Platelet-rich plasma therapy for skin rejuvenation and collagen stimulation",
    dateTime: "May 24, 2024 at 2:00 PM",
    practitioner: "Emma Taylor",
    status: "In Progress",
    price: 450.0,
    paidAmount: 225.0,
    consent: "Signed",
    aftercare: "Not shared",
    treatmentArea: "Full Face & Neck",
    duration: "90 minutes",
    clinicalNotes: "Blood draw completed successfully. 12ml collected. PRP separation in progress. Patient comfortable with topical anaesthetic applied 20 minutes ago.",
    aftercareNotes: "",
    shareWithPatient: false,
    additionalNotes: [
      {
        id: "1",
        content: "Treatment started at 2:05 PM. Blood draw completed with no complications. Client tolerated numbing cream well.",
        author: "Emma Taylor",
        date: "May 24, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "2",
        content: "Pre-treatment: Verified client has fasted for 4 hours. Hydration adequate. No blood thinners taken.",
        author: "Emma Taylor",
        date: "May 24, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "3",
        content: "Client history: Previous PRP treatment 8 months ago with excellent results. No adverse reactions reported.",
        author: "Dr Saleema",
        date: "May 20, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Emma Taylor" }, // Baseline face
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Emma Taylor" }, // Detailed lines
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Emma Taylor" }, // Revitalized after
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Emma Taylor" }, // Radiant glow
    ],
    documents: [
      { id: "1", name: "PRP Treatment Consent", category: "Consent", status: "Signed", dateUploaded: "May 20, 2024", signedBy: "Emma Clark", signedDate: "May 22, 2024", pages: 4 },
      { id: "2", name: "Blood Test Results", category: "Medical", status: "Completed", dateUploaded: "May 18, 2024", signedBy: "Dr Saleema", signedDate: "May 18, 2024", pages: 2 },
      { id: "3", name: "Medical Clearance Form", category: "Medical History", status: "Completed", dateUploaded: "May 19, 2024", signedBy: "Emma Clark", signedDate: "May 19, 2024", pages: 1 },
      { id: "4", name: "PRP Aftercare Guide", category: "Aftercare", status: "Draft", dateUploaded: "May 24, 2024", pages: 2 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 10, 2024 at 11:00 AM", type: "system" },
      { id: "2", action: "Blood test results uploaded", user: "Reception", time: "May 18, 2024 at 3:00 PM", type: "document" },
      { id: "3", action: "Medical clearance confirmed", user: "Dr Saleema", time: "May 19, 2024 at 10:00 AM", type: "user" },
      { id: "4", action: "Deposit payment received - £225.00", user: "Reception", time: "May 20, 2024 at 2:00 PM", type: "payment" },
      { id: "5", action: "Consent form signed", user: "Emma Clark", time: "May 22, 2024 at 5:00 PM", type: "document" },
      { id: "6", action: "Treatment started", user: "Emma Taylor", time: "May 24, 2024 at 2:05 PM", type: "status" },
      { id: "7", action: "Blood draw completed", user: "Emma Taylor", time: "May 24, 2024 at 2:15 PM", type: "user" },
      { id: "8", action: "PRP preparation started", user: "Emma Taylor", time: "May 24, 2024 at 2:20 PM", type: "user" },
    ],
  },
  "5": {
    id: "5",
    client: {
      name: "Michael Davis",
      initials: "MD",
      avatarBg: "bg-emerald-100 text-emerald-600",
      email: "michael.davis@email.com",
      phone: "+44 7700 905678",
    },
    treatment: "Laser Skin Resurfacing",
    treatmentDescription: "Fractional CO2 laser treatment for acne scars and skin texture improvement",
    dateTime: "May 24, 2024 at 11:00 AM",
    practitioner: "Dr Saleema",
    status: "In Progress",
    price: 800.0,
    paidAmount: 400.0,
    consent: "Signed",
    aftercare: "Not shared",
    treatmentArea: "Cheeks & Jawline",
    duration: "60 minutes",
    clinicalNotes: "First pass completed at 15mJ, 200 spots/cm². Client tolerating well. Slight erythema noted as expected. Proceeding with second pass.",
    aftercareNotes: "",
    shareWithPatient: false,
    additionalNotes: [
      {
        id: "1",
        content: "Treatment parameters: CO2 fractional laser, 15mJ, 200 spots/cm², 2 passes planned. Numbing cream applied for 45 minutes pre-treatment.",
        author: "Dr Saleema",
        date: "May 24, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "2",
        content: "Client has grade III acne scarring on cheeks. Treatment plan: 3 sessions, 6 weeks apart.",
        author: "Dr Saleema",
        date: "May 15, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "3",
        content: "Patch test completed on May 10th with good results. No adverse reactions. Cleared for full treatment.",
        author: "Dr Saleema",
        date: "May 12, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "4",
        content: "Pre-treatment prep: Stopped retinoids 2 weeks ago, avoided sun exposure, completed antiviral prophylaxis.",
        author: "Emma Taylor",
        date: "May 20, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1619451334792-150fd785ee74?w=400&h=400&fit=crop", date: "May 15, 2024", practitioner: "Dr Saleema" }, // Acne scarring before
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=400&fit=crop", date: "May 15, 2024", practitioner: "Dr Saleema" }, // Texture detail
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Dr Saleema" }, // Improved after
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Dr Saleema" }, // Radiant skin
    ],
    documents: [
      { id: "1", name: "Laser Resurfacing Consent", category: "Consent", status: "Signed", dateUploaded: "May 18, 2024", signedBy: "Michael Davis", signedDate: "May 19, 2024", pages: 5 },
      { id: "2", name: "Patch Test Results", category: "Medical", status: "Completed", dateUploaded: "May 12, 2024", signedBy: "Dr Saleema", signedDate: "May 12, 2024", pages: 1 },
      { id: "3", name: "Treatment Plan - Acne Scarring", category: "Treatment Plan", status: "Completed", dateUploaded: "May 15, 2024", signedBy: "Dr Saleema", signedDate: "May 15, 2024", pages: 3 },
      { id: "4", name: "Post-Laser Care Instructions", category: "Aftercare", status: "Draft", dateUploaded: "May 24, 2024", pages: 2 },
      { id: "5", name: "Antiviral Prescription", category: "Medical", status: "Completed", dateUploaded: "May 20, 2024", signedBy: "Dr Saleema", signedDate: "May 20, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 8, 2024 at 10:00 AM", type: "system" },
      { id: "2", action: "Patch test scheduled", user: "Reception", time: "May 8, 2024 at 10:30 AM", type: "user" },
      { id: "3", action: "Patch test completed - cleared for treatment", user: "Dr Saleema", time: "May 12, 2024 at 11:00 AM", type: "user" },
      { id: "4", action: "Treatment plan created", user: "Dr Saleema", time: "May 15, 2024 at 3:00 PM", type: "document" },
      { id: "5", action: "Deposit payment received - £400.00", user: "Reception", time: "May 16, 2024 at 2:00 PM", type: "payment" },
      { id: "6", action: "Consent form signed", user: "Michael Davis", time: "May 19, 2024 at 6:00 PM", type: "document" },
      { id: "7", action: "Antiviral prescription issued", user: "Dr Saleema", time: "May 20, 2024 at 9:00 AM", type: "user" },
      { id: "8", action: "Treatment started", user: "Dr Saleema", time: "May 24, 2024 at 11:05 AM", type: "status" },
      { id: "9", action: "First laser pass completed", user: "Dr Saleema", time: "May 24, 2024 at 11:25 AM", type: "user" },
    ],
  },
  "6": {
    id: "6",
    client: {
      name: "Rachel Adams",
      initials: "RA",
      avatarBg: "bg-rose-100 text-rose-600",
      email: "rachel.adams@email.com",
      phone: "+44 7700 909012",
    },
    treatment: "Dermal Fillers",
    treatmentDescription: "Hyaluronic acid filler injections for cheek augmentation and facial contouring",
    dateTime: "May 24, 2024 at 3:45 PM",
    practitioner: "Dr Saleema",
    status: "In Progress",
    price: 650.0,
    paidAmount: 650.0,
    consent: "Signed",
    aftercare: "Not shared",
    treatmentArea: "Cheeks & Midface",
    duration: "45 minutes",
    clinicalNotes: "2ml Juvederm Voluma used (1ml per cheek). Excellent placement achieved. Minimal swelling observed. Client very pleased with initial results.",
    aftercareNotes: "",
    shareWithPatient: false,
    additionalNotes: [
      {
        id: "1",
        content: "Product used: Juvederm Voluma 2ml. Injection technique: deep bolus with cannula for cheek augmentation.",
        author: "Dr Saleema",
        date: "May 24, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "2",
        content: "Client goals: restore volume loss in midface, subtle lift effect. No previous filler history. Natural look desired.",
        author: "Dr Saleema",
        date: "May 20, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "3",
        content: "Consultation completed. Treatment plan agreed: 2ml Voluma for cheeks. May need additional 1ml in 2 weeks for optimal results.",
        author: "Dr Saleema",
        date: "May 18, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1600609842388-3e2ed2a1454a?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Dr Saleema" }, // Lips before
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Dr Saleema" }, // Detailed lips
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Dr Saleema" }, // Plump after
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=400&h=400&fit=crop", date: "May 24, 2024", practitioner: "Dr Saleema" }, // Natural plump
    ],
    documents: [
      { id: "1", name: "Dermal Filler Consent Form", category: "Consent", status: "Signed", dateUploaded: "May 20, 2024", signedBy: "Rachel Adams", signedDate: "May 22, 2024", pages: 4 },
      { id: "2", name: "Allergy Questionnaire", category: "Medical History", status: "Completed", dateUploaded: "May 18, 2024", signedBy: "Rachel Adams", signedDate: "May 18, 2024", pages: 1 },
      { id: "3", name: "Facial Assessment Report", category: "Assessment", status: "Completed", dateUploaded: "May 20, 2024", signedBy: "Dr Saleema", signedDate: "May 20, 2024", pages: 2 },
      { id: "4", name: "Filler Aftercare Instructions", category: "Aftercare", status: "Draft", dateUploaded: "May 24, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 15, 2024 at 4:00 PM", type: "system" },
      { id: "2", action: "Consultation completed", user: "Dr Saleema", time: "May 18, 2024 at 11:00 AM", type: "user" },
      { id: "3", action: "Full payment received - £650.00", user: "Reception", time: "May 20, 2024 at 10:00 AM", type: "payment" },
      { id: "4", action: "Facial assessment completed", user: "Dr Saleema", time: "May 20, 2024 at 10:30 AM", type: "user" },
      { id: "5", action: "Consent form signed", user: "Rachel Adams", time: "May 22, 2024 at 8:00 PM", type: "document" },
      { id: "6", action: "Pre-treatment photos captured", user: "Dr Saleema", time: "May 24, 2024 at 3:50 PM", type: "user" },
      { id: "7", action: "Treatment started", user: "Dr Saleema", time: "May 24, 2024 at 4:00 PM", type: "status" },
      { id: "8", action: "Filler injection completed", user: "Dr Saleema", time: "May 24, 2024 at 4:30 PM", type: "user" },
      { id: "9", action: "Post-treatment photos captured", user: "Dr Saleema", time: "May 24, 2024 at 4:35 PM", type: "user" },
    ],
  },
  "7": {
    id: "7",
    client: {
      name: "Sophia Green",
      initials: "SG",
      avatarBg: "bg-teal-100 text-teal-600",
      email: "sophia.green@email.com",
      phone: "+44 7700 903456",
    },
    treatment: "Microneedling",
    treatmentDescription: "Advanced microneedling with growth factor serum for skin rejuvenation",
    dateTime: "May 22, 2024 at 10:00 AM",
    practitioner: "Emma Taylor",
    status: "Completed",
    price: 280.0,
    paidAmount: 280.0,
    consent: "Signed",
    aftercare: "Shared",
    treatmentArea: "Full Face",
    duration: "45 minutes",
    nextAppointment: "June 19, 2024 at 10:00 AM",
    clinicalNotes: "Treatment completed successfully. Needle depth: 1.5mm on cheeks, 1.0mm on forehead and nose. Growth factor serum applied throughout. Mild pinpoint bleeding noted - normal response. Post-treatment recovery serum applied.",
    aftercareNotes: "Avoid direct sun exposure for 7 days. Apply provided hydrating serum twice daily. Do not use active ingredients (retinoids, AHAs, BHAs) for 5 days. Expect mild redness for 24-48 hours.",
    shareWithPatient: true,
    additionalNotes: [
      {
        id: "1",
        content: "Treatment completed. Client tolerated procedure well with minimal discomfort. Excellent pinpoint bleeding response indicating good treatment depth.",
        author: "Emma Taylor",
        date: "May 22, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "2",
        content: "Post-treatment: Soothing mask applied for 15 minutes. Client given aftercare kit including gentle cleanser and recovery serum.",
        author: "Emma Taylor",
        date: "May 22, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "3",
        content: "Follow-up booked for 4 weeks. This is session 2 of 3 in the treatment series.",
        author: "Reception",
        date: "May 22, 2024",
        avatarBg: "bg-amber-100 text-amber-600",
      },
      {
        id: "4",
        content: "Pre-treatment skin analysis: Fitzpatrick type II, mild dehydration, fine lines around eyes and forehead.",
        author: "Emma Taylor",
        date: "May 20, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1619451334792-150fd785ee74?w=400&h=400&fit=crop", date: "May 22, 2024", practitioner: "Emma Taylor" }, // Scars before
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=400&fit=crop", date: "May 22, 2024", practitioner: "Emma Taylor" }, // Skin lines
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop", date: "May 22, 2024", practitioner: "Emma Taylor" }, // Improved after
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=400&fit=crop", date: "May 22, 2024", practitioner: "Emma Taylor" }, // Radiant skin
      { id: "5", type: "before", url: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop", date: "April 24, 2024", practitioner: "Emma Taylor" }, // Male scarring
      { id: "6", type: "after", url: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop", date: "April 24, 2024", practitioner: "Emma Taylor" }, // Smoother after
    ],
    documents: [
      { id: "1", name: "Microneedling Consent Form", category: "Consent", status: "Signed", dateUploaded: "May 18, 2024", signedBy: "Sophia Green", signedDate: "May 19, 2024", pages: 2 },
      { id: "2", name: "Medical History Update", category: "Medical History", status: "Completed", dateUploaded: "May 18, 2024", signedBy: "Sophia Green", signedDate: "May 18, 2024", pages: 1 },
      { id: "3", name: "Microneedling Aftercare Guide", category: "Aftercare", status: "Completed", dateUploaded: "May 22, 2024", signedBy: "Emma Taylor", signedDate: "May 22, 2024", pages: 1 },
      { id: "4", name: "Treatment Summary Report", category: "Report", status: "Completed", dateUploaded: "May 22, 2024", signedBy: "Emma Taylor", signedDate: "May 22, 2024", pages: 2 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 12, 2024 at 9:00 AM", type: "system" },
      { id: "2", action: "Appointment confirmed", user: "Sophia Green", time: "May 12, 2024 at 9:30 AM", type: "user" },
      { id: "3", action: "Full payment received - £280.00", user: "Reception", time: "May 15, 2024 at 2:00 PM", type: "payment" },
      { id: "4", action: "Consent form signed", user: "Sophia Green", time: "May 19, 2024 at 7:00 PM", type: "document" },
      { id: "5", action: "Pre-treatment photos uploaded", user: "Emma Taylor", time: "May 22, 2024 at 10:05 AM", type: "user" },
      { id: "6", action: "Treatment started", user: "Emma Taylor", time: "May 22, 2024 at 10:10 AM", type: "status" },
      { id: "7", action: "Treatment completed", user: "Emma Taylor", time: "May 22, 2024 at 10:55 AM", type: "status" },
      { id: "8", action: "Post-treatment photos uploaded", user: "Emma Taylor", time: "May 22, 2024 at 11:00 AM", type: "user" },
      { id: "9", action: "Aftercare instructions shared with client", user: "Emma Taylor", time: "May 22, 2024 at 11:05 AM", type: "document" },
      { id: "10", action: "Follow-up appointment booked", user: "Reception", time: "May 22, 2024 at 11:15 AM", type: "user" },
      { id: "11", action: "Treatment summary report generated", user: "Emma Taylor", time: "May 22, 2024 at 11:30 AM", type: "document" },
    ],
  },
  "8": {
    id: "8",
    client: {
      name: "David Wilson",
      initials: "DW",
      avatarBg: "bg-indigo-100 text-indigo-600",
      email: "david.wilson@email.com",
      phone: "+44 7700 907890",
    },
    treatment: "Lip Filler",
    treatmentDescription: "Hyaluronic acid lip augmentation for natural volume enhancement",
    dateTime: "May 20, 2024 at 1:30 PM",
    practitioner: "Emma Taylor",
    status: "Completed",
    price: 320.0,
    paidAmount: 320.0,
    consent: "Signed",
    aftercare: "Shared",
    treatmentArea: "Lips",
    duration: "30 minutes",
    nextAppointment: "August 15, 2024 at 2:00 PM",
    clinicalNotes: "1ml Juvederm Ultra Smile used. Technique: linear threading with slight bolus at cupid's bow. Natural enhancement achieved with improved definition. Client extremely satisfied with results.",
    aftercareNotes: "Avoid touching or rubbing lips for 24 hours. Apply ice if swelling occurs. Do not exercise for 24 hours. Avoid alcohol for 48 hours. Some bruising may occur - this is normal.",
    shareWithPatient: true,
    additionalNotes: [
      {
        id: "1",
        content: "Excellent outcome achieved. 1ml Juvederm Ultra Smile provided subtle, natural enhancement as requested. Before/after photos taken for records.",
        author: "Emma Taylor",
        date: "May 20, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "2",
        content: "2-week follow-up call: Client reports full settling, no complications. Very happy with results. May return for additional 0.5ml in future.",
        author: "Emma Taylor",
        date: "June 3, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "3",
        content: "Client goals: subtle volume increase, improved definition of cupid's bow, natural look. First time having lip filler.",
        author: "Emma Taylor",
        date: "May 15, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1507081323647-4d250478b919?w=400&h=400&fit=crop", date: "May 20, 2024", practitioner: "Emma Taylor" },
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop", date: "May 20, 2024", practitioner: "Emma Taylor" },
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop", date: "May 20, 2024", practitioner: "Emma Taylor" },
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=400&h=400&fit=crop", date: "May 20, 2024", practitioner: "Emma Taylor" },
    ],
    documents: [
      { id: "1", name: "Lip Filler Consent Form", category: "Consent", status: "Signed", dateUploaded: "May 15, 2024", signedBy: "David Wilson", signedDate: "May 17, 2024", pages: 3 },
      { id: "2", name: "Medical History Form", category: "Medical History", status: "Completed", dateUploaded: "May 15, 2024", signedBy: "David Wilson", signedDate: "May 15, 2024", pages: 2 },
      { id: "3", name: "Lip Filler Aftercare Guide", category: "Aftercare", status: "Completed", dateUploaded: "May 20, 2024", signedBy: "Emma Taylor", signedDate: "May 20, 2024", pages: 1 },
      { id: "4", name: "Treatment Summary", category: "Report", status: "Completed", dateUploaded: "May 20, 2024", signedBy: "Emma Taylor", signedDate: "May 20, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 10, 2024 at 3:00 PM", type: "system" },
      { id: "2", action: "Full payment received - £320.00", user: "Reception", time: "May 12, 2024 at 11:00 AM", type: "payment" },
      { id: "3", action: "Consent form sent to client", user: "Emma Taylor", time: "May 15, 2024 at 9:00 AM", type: "document" },
      { id: "4", action: "Consent form signed", user: "David Wilson", time: "May 17, 2024 at 6:30 PM", type: "document" },
      { id: "5", action: "Pre-treatment photos captured", user: "Emma Taylor", time: "May 20, 2024 at 1:35 PM", type: "user" },
      { id: "6", action: "Treatment started", user: "Emma Taylor", time: "May 20, 2024 at 1:40 PM", type: "status" },
      { id: "7", action: "Treatment completed", user: "Emma Taylor", time: "May 20, 2024 at 2:10 PM", type: "status" },
      { id: "8", action: "Post-treatment photos captured", user: "Emma Taylor", time: "May 20, 2024 at 2:15 PM", type: "user" },
      { id: "9", action: "Aftercare instructions shared with client", user: "Emma Taylor", time: "May 20, 2024 at 2:20 PM", type: "document" },
      { id: "10", action: "2-week follow-up call completed", user: "Emma Taylor", time: "June 3, 2024 at 10:00 AM", type: "user" },
    ],
  },
  "9": {
    id: "9",
    client: {
      name: "Olivia Martinez",
      initials: "OM",
      avatarBg: "bg-cyan-100 text-cyan-600",
      email: "olivia.martinez@email.com",
      phone: "+44 7700 904567",
    },
    treatment: "Botox - Upper Face",
    treatmentDescription: "Botulinum toxin injections for dynamic wrinkles in forehead, glabella, and crow's feet",
    dateTime: "May 18, 2024 at 3:00 PM",
    practitioner: "Dr Saleema",
    status: "Completed",
    price: 380.0,
    paidAmount: 380.0,
    consent: "Signed",
    aftercare: "Shared",
    treatmentArea: "Forehead, Glabella, Crow's Feet",
    duration: "30 minutes",
    nextAppointment: "August 18, 2024 at 3:00 PM",
    clinicalNotes: "Total 48 units Botox administered. Distribution: Forehead 12u, Glabella 20u, Crow's feet 16u (8u per side). Standard injection technique with no complications. Client tolerated well.",
    aftercareNotes: "Do not lie down for 4 hours. Avoid rubbing treated areas for 24 hours. No strenuous exercise for 24 hours. Results visible in 3-7 days, full effect in 2 weeks.",
    shareWithPatient: true,
    additionalNotes: [
      {
        id: "1",
        content: "Treatment completed successfully. 48 units total across three areas. Client experienced mild discomfort during glabella injections but overall tolerated well.",
        author: "Dr Saleema",
        date: "May 18, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "2",
        content: "Pre-treatment assessment: Moderate forehead lines, deep glabellar lines (11s), mild crow's feet. Discussed expectations - approximately 70% reduction expected.",
        author: "Dr Saleema",
        date: "May 15, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "3",
        content: "2-week follow-up: Excellent results achieved. Client very happy. Minor asymmetry in left brow noted - will reassess at next appointment.",
        author: "Dr Saleema",
        date: "June 1, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop", date: "May 18, 2024", practitioner: "Dr Saleema" },
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop", date: "May 18, 2024", practitioner: "Dr Saleema" },
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=400&fit=crop", date: "June 1, 2024", practitioner: "Dr Saleema" },
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=400&h=400&fit=crop", date: "June 1, 2024", practitioner: "Dr Saleema" },
    ],
    documents: [
      { id: "1", name: "Botox Treatment Consent", category: "Consent", status: "Signed", dateUploaded: "May 15, 2024", signedBy: "Olivia Martinez", signedDate: "May 16, 2024", pages: 3 },
      { id: "2", name: "Medical History Form", category: "Medical History", status: "Completed", dateUploaded: "May 15, 2024", signedBy: "Olivia Martinez", signedDate: "May 15, 2024", pages: 2 },
      { id: "3", name: "Botox Aftercare Guide", category: "Aftercare", status: "Completed", dateUploaded: "May 18, 2024", signedBy: "Dr Saleema", signedDate: "May 18, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment record created", user: "System", time: "May 10, 2024 at 2:00 PM", type: "system" },
      { id: "2", action: "Full payment received - £380.00", user: "Reception", time: "May 12, 2024 at 3:00 PM", type: "payment" },
      { id: "3", action: "Consultation completed", user: "Dr Saleema", time: "May 15, 2024 at 10:00 AM", type: "user" },
      { id: "4", action: "Consent form signed", user: "Olivia Martinez", time: "May 16, 2024 at 9:00 PM", type: "document" },
      { id: "5", action: "Treatment started", user: "Dr Saleema", time: "May 18, 2024 at 3:05 PM", type: "status" },
      { id: "6", action: "Treatment completed", user: "Dr Saleema", time: "May 18, 2024 at 3:35 PM", type: "status" },
      { id: "7", action: "Aftercare instructions shared", user: "Dr Saleema", time: "May 18, 2024 at 3:40 PM", type: "document" },
      { id: "8", action: "2-week follow-up completed", user: "Dr Saleema", time: "June 1, 2024 at 11:00 AM", type: "user" },
    ],
  },
  "10": {
    id: "10",
    client: {
      name: "William Thompson",
      initials: "WT",
      avatarBg: "bg-violet-100 text-violet-600",
      email: "william.thompson@email.com",
      phone: "+44 7700 906789",
    },
    treatment: "LED Light Therapy",
    treatmentDescription: "Non-invasive LED phototherapy for skin rejuvenation and acne reduction",
    dateTime: "May 15, 2024 at 11:30 AM",
    practitioner: "Emma Taylor",
    status: "Completed",
    price: 95.0,
    paidAmount: 95.0,
    consent: "Signed",
    aftercare: "Shared",
    treatmentArea: "Full Face",
    duration: "30 minutes",
    nextAppointment: "May 29, 2024 at 11:30 AM",
    clinicalNotes: "30-minute LED session completed. Red and blue light combination used. Client reported comfortable experience. No adverse reactions. Skin appeared slightly flushed post-treatment - normal response.",
    aftercareNotes: "Apply hydrating serum immediately after treatment. Avoid harsh skincare products for 24 hours. Stay hydrated. Can resume normal activities immediately.",
    shareWithPatient: true,
    additionalNotes: [
      {
        id: "1",
        content: "Session 4 of 6 in the LED therapy course. Client reports improved skin texture and reduced acne breakouts since starting treatment.",
        author: "Emma Taylor",
        date: "May 15, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "2",
        content: "Progress review: Significant improvement in inflammatory acne. Recommend continuing with remaining sessions.",
        author: "Emma Taylor",
        date: "May 15, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop", date: "April 17, 2024", practitioner: "Emma Taylor" },
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1513956589380-bad6acb9b9d4?w=400&h=400&fit=crop", date: "April 17, 2024", practitioner: "Emma Taylor" },
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop", date: "May 15, 2024", practitioner: "Emma Taylor" },
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop", date: "May 15, 2024", practitioner: "Emma Taylor" },
    ],
    documents: [
      { id: "1", name: "LED Therapy Consent", category: "Consent", status: "Signed", dateUploaded: "April 10, 2024", signedBy: "William Thompson", signedDate: "April 10, 2024", pages: 2 },
      { id: "2", name: "Skin Assessment Report", category: "Assessment", status: "Completed", dateUploaded: "April 17, 2024", signedBy: "Emma Taylor", signedDate: "April 17, 2024", pages: 1 },
      { id: "3", name: "LED Aftercare Instructions", category: "Aftercare", status: "Completed", dateUploaded: "May 15, 2024", signedBy: "Emma Taylor", signedDate: "May 15, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment course started", user: "System", time: "April 10, 2024 at 9:00 AM", type: "system" },
      { id: "2", action: "Session 4 payment received - £95.00", user: "Reception", time: "May 15, 2024 at 11:00 AM", type: "payment" },
      { id: "3", action: "Treatment started", user: "Emma Taylor", time: "May 15, 2024 at 11:35 AM", type: "status" },
      { id: "4", action: "Treatment completed", user: "Emma Taylor", time: "May 15, 2024 at 12:05 PM", type: "status" },
      { id: "5", action: "Progress notes updated", user: "Emma Taylor", time: "May 15, 2024 at 12:10 PM", type: "user" },
      { id: "6", action: "Next session booked", user: "Reception", time: "May 15, 2024 at 12:15 PM", type: "user" },
    ],
  },
  "11": {
    id: "11",
    client: {
      name: "Isabella Johnson",
      initials: "IJ",
      avatarBg: "bg-orange-100 text-orange-600",
      email: "isabella.johnson@email.com",
      phone: "+44 7700 908901",
    },
    treatment: "Skin Tightening",
    treatmentDescription: "Radiofrequency skin tightening treatment for face and neck rejuvenation",
    dateTime: "May 12, 2024 at 2:30 PM",
    practitioner: "Dr Saleema",
    status: "Completed",
    price: 550.0,
    paidAmount: 550.0,
    consent: "Signed",
    aftercare: "Shared",
    treatmentArea: "Face & Neck",
    duration: "75 minutes",
    nextAppointment: "June 9, 2024 at 2:30 PM",
    clinicalNotes: "Full radiofrequency treatment completed. Temperature maintained at 40-42°C throughout. Focus areas: jawline, submental region, neck. Excellent tissue response observed. Client tolerated treatment well with mild warmth sensation.",
    aftercareNotes: "Mild redness may persist for 2-4 hours. Stay well hydrated. Avoid extreme heat (saunas, hot baths) for 48 hours. Use SPF 50 daily. Some clients experience mild swelling - this is normal and resolves within 24 hours.",
    shareWithPatient: true,
    additionalNotes: [
      {
        id: "1",
        content: "Session 2 of 4. Good collagen stimulation response observed. Client noted visible improvement in jawline definition after first session.",
        author: "Dr Saleema",
        date: "May 12, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "2",
        content: "Pre-treatment assessment: Fitzpatrick type III, mild skin laxity on lower face and neck. Treatment settings adjusted for optimal results.",
        author: "Dr Saleema",
        date: "May 12, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
      {
        id: "3",
        content: "Initial consultation: Client concerned about early signs of jowling. Recommended 4-session RF treatment course with 4-week intervals.",
        author: "Dr Saleema",
        date: "April 14, 2024",
        avatarBg: "bg-blue-100 text-blue-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1559599101-f09722fb4948?w=400&h=400&fit=crop", date: "April 14, 2024", practitioner: "Dr Saleema" },
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop", date: "April 14, 2024", practitioner: "Dr Saleema" },
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1612908689462-b5e0a8a2d4e7?w=400&h=400&fit=crop", date: "May 12, 2024", practitioner: "Dr Saleema" },
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400&h=400&fit=crop", date: "May 12, 2024", practitioner: "Dr Saleema" },
    ],
    documents: [
      { id: "1", name: "RF Skin Tightening Consent", category: "Consent", status: "Signed", dateUploaded: "April 10, 2024", signedBy: "Isabella Johnson", signedDate: "April 12, 2024", pages: 3 },
      { id: "2", name: "Medical History Questionnaire", category: "Medical History", status: "Completed", dateUploaded: "April 10, 2024", signedBy: "Isabella Johnson", signedDate: "April 10, 2024", pages: 2 },
      { id: "3", name: "Treatment Plan - Skin Tightening", category: "Treatment Plan", status: "Completed", dateUploaded: "April 14, 2024", signedBy: "Dr Saleema", signedDate: "April 14, 2024", pages: 2 },
      { id: "4", name: "RF Aftercare Guide", category: "Aftercare", status: "Completed", dateUploaded: "May 12, 2024", signedBy: "Dr Saleema", signedDate: "May 12, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment course created", user: "System", time: "April 10, 2024 at 10:00 AM", type: "system" },
      { id: "2", action: "Consultation completed", user: "Dr Saleema", time: "April 14, 2024 at 3:00 PM", type: "user" },
      { id: "3", action: "Session 1 completed", user: "Dr Saleema", time: "April 14, 2024 at 4:30 PM", type: "status" },
      { id: "4", action: "Session 2 payment received - £550.00", user: "Reception", time: "May 12, 2024 at 2:00 PM", type: "payment" },
      { id: "5", action: "Treatment started", user: "Dr Saleema", time: "May 12, 2024 at 2:35 PM", type: "status" },
      { id: "6", action: "Treatment completed", user: "Dr Saleema", time: "May 12, 2024 at 3:50 PM", type: "status" },
      { id: "7", action: "Progress photos captured", user: "Dr Saleema", time: "May 12, 2024 at 3:55 PM", type: "user" },
      { id: "8", action: "Aftercare shared with client", user: "Dr Saleema", time: "May 12, 2024 at 4:00 PM", type: "document" },
    ],
  },
  "12": {
    id: "12",
    client: {
      name: "Ethan Brown",
      initials: "EB",
      avatarBg: "bg-lime-100 text-lime-600",
      email: "ethan.brown@email.com",
      phone: "+44 7700 902345",
    },
    treatment: "Acne Treatment",
    treatmentDescription: "Comprehensive acne treatment including extraction, LED therapy, and medical-grade skincare",
    dateTime: "May 10, 2024 at 10:00 AM",
    practitioner: "Emma Taylor",
    status: "Completed",
    price: 180.0,
    paidAmount: 180.0,
    consent: "Signed",
    aftercare: "Shared",
    treatmentArea: "Full Face",
    duration: "60 minutes",
    nextAppointment: "May 24, 2024 at 10:00 AM",
    clinicalNotes: "Deep cleansing facial with professional extractions performed. Focused on T-zone where most active lesions present. Followed by 20-minute blue LED therapy for bacterial reduction. Calming mask applied to reduce inflammation. Client tolerated extractions well.",
    aftercareNotes: "Keep skin clean but avoid over-washing. Apply prescribed benzoyl peroxide spot treatment at night. Do not pick at skin. Expect some purging in next 1-2 weeks as skin clears. Return in 2 weeks for follow-up treatment.",
    shareWithPatient: true,
    additionalNotes: [
      {
        id: "1",
        content: "Session 3 of the acne treatment programme. Significant improvement noted - active lesions reduced by approximately 60% since treatment start.",
        author: "Emma Taylor",
        date: "May 10, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "2",
        content: "Extraction notes: Multiple closed comedones removed from forehead and chin. Several inflammatory papules drained. No cystic lesions requiring referral.",
        author: "Emma Taylor",
        date: "May 10, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "3",
        content: "Home care compliance check: Client has been following prescribed skincare routine consistently. Recommend continuing current protocol.",
        author: "Emma Taylor",
        date: "May 10, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
      {
        id: "4",
        content: "Initial assessment: Grade II-III acne with mix of comedonal and inflammatory lesions. Recommended 6-session treatment course.",
        author: "Emma Taylor",
        date: "April 12, 2024",
        avatarBg: "bg-pink-100 text-pink-600",
      },
    ],
    photos: [
      { id: "1", type: "before", url: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop", date: "April 12, 2024", practitioner: "Emma Taylor" },
      { id: "2", type: "before", url: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop", date: "April 12, 2024", practitioner: "Emma Taylor" },
      { id: "3", type: "after", url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop", date: "April 26, 2024", practitioner: "Emma Taylor" },
      { id: "4", type: "after", url: "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=400&h=400&fit=crop", date: "May 10, 2024", practitioner: "Emma Taylor" },
    ],
    documents: [
      { id: "1", name: "Acne Treatment Consent", category: "Consent", status: "Signed", dateUploaded: "April 10, 2024", signedBy: "Ethan Brown", signedDate: "April 11, 2024", pages: 2 },
      { id: "2", name: "Skin Analysis Report", category: "Assessment", status: "Completed", dateUploaded: "April 12, 2024", signedBy: "Emma Taylor", signedDate: "April 12, 2024", pages: 2 },
      { id: "3", name: "Acne Treatment Plan", category: "Treatment Plan", status: "Completed", dateUploaded: "April 12, 2024", signedBy: "Emma Taylor", signedDate: "April 12, 2024", pages: 2 },
      { id: "4", name: "Skincare Prescription", category: "Prescription", status: "Completed", dateUploaded: "April 12, 2024", signedBy: "Emma Taylor", signedDate: "April 12, 2024", pages: 1 },
      { id: "5", name: "Acne Aftercare Instructions", category: "Aftercare", status: "Completed", dateUploaded: "May 10, 2024", signedBy: "Emma Taylor", signedDate: "May 10, 2024", pages: 1 },
    ],
    logs: [
      { id: "1", action: "Treatment programme created", user: "System", time: "April 10, 2024 at 9:00 AM", type: "system" },
      { id: "2", action: "Initial consultation completed", user: "Emma Taylor", time: "April 12, 2024 at 10:00 AM", type: "user" },
      { id: "3", action: "Skincare products prescribed", user: "Emma Taylor", time: "April 12, 2024 at 10:30 AM", type: "user" },
      { id: "4", action: "Session 1 completed", user: "Emma Taylor", time: "April 12, 2024 at 11:30 AM", type: "status" },
      { id: "5", action: "Session 2 completed", user: "Emma Taylor", time: "April 26, 2024 at 11:00 AM", type: "status" },
      { id: "6", action: "Session 3 payment received - £180.00", user: "Reception", time: "May 10, 2024 at 9:30 AM", type: "payment" },
      { id: "7", action: "Treatment started", user: "Emma Taylor", time: "May 10, 2024 at 10:05 AM", type: "status" },
      { id: "8", action: "Extractions completed", user: "Emma Taylor", time: "May 10, 2024 at 10:30 AM", type: "user" },
      { id: "9", action: "LED therapy completed", user: "Emma Taylor", time: "May 10, 2024 at 10:50 AM", type: "user" },
      { id: "10", action: "Treatment completed", user: "Emma Taylor", time: "May 10, 2024 at 11:00 AM", type: "status" },
      { id: "11", action: "Progress photos captured", user: "Emma Taylor", time: "May 10, 2024 at 11:05 AM", type: "user" },
      { id: "12", action: "Aftercare instructions shared", user: "Emma Taylor", time: "May 10, 2024 at 11:10 AM", type: "document" },
    ],
  },
};

// Get default treatment for IDs not in static data
const getDefaultTreatment = (id: string): TreatmentDetail => {
  const baseTreatment = staticTreatmentData["1"];
  return {
    ...baseTreatment,
    id,
    logs: baseTreatment.logs.map((log, index) => ({ ...log, id: `default-${index}` })),
  };
};

// Status Badge Component
const StatusBadge = ({ status }: { status: TreatmentStatus }) => {
  const styles = {
    Upcoming: "text-[#704aaf]",
    "In Progress": "text-[#e6a700]",
    Completed: "text-[#0d7a3e]",
  };

  return <span className={`text-sm font-medium ${styles[status]}`}>{status}</span>;
};

// Document Status Badge
const DocumentStatusBadge = ({ status }: { status: DocumentStatus }) => {
  const styles = {
    Signed: "bg-[#e6f7ed] text-[#0d7a3e] border border-[#b8e6cd]",
    Completed: "bg-[#e6f7ed] text-[#0d7a3e] border border-[#b8e6cd]",
    Draft: "bg-gray-100 text-[#6b6b6b] border border-gray-200",
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${styles[status]}`}>
      {(status === "Signed" || status === "Completed") && <Check className="w-3 h-3" />}
      {status === "Draft" && <FileText className="w-3 h-3" />}
      {status}
    </span>
  );
};

// Consent Status Component
const ConsentBadge = ({ status }: { status: ConsentStatus }) => {
  if (status === "Pending") {
    return (
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-[#e6a700]" />
        <span className="text-[#e6a700] font-medium">Pending*</span>
      </div>
    );
  }
  if (status === "Signed") {
    return (
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-[#0d7a3e]" />
        <span className="text-[#0d7a3e] font-medium">Signed</span>
      </div>
    );
  }
  return <span className="text-[#6b6b6b]">Not Required</span>;
};

// Toggle Switch Component
const ToggleSwitch = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <button
    onClick={() => onChange(!checked)}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-[#704aaf]" : "bg-gray-300"}`}
  >
    <span
      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`}
    />
  </button>
);

// Overview Tab Content
const OverviewTab = ({
  treatment,
  onCompleteTreatment,
  onStartTreatment,
  onRebook,
  onTakePayment,
}: {
  treatment: TreatmentDetail;
  onStartTreatment: () => void;
  onCompleteTreatment: () => void;
  onRebook: () => void;
  onTakePayment: () => void;
}) => {
  const balance = treatment.price - treatment.paidAmount;

  return (
    <div className="space-y-6">
      {/* Treatment Summary Card */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <h3 className="text-base font-semibold text-[#2d2d2d] px-5 py-4 border-b border-[#f0ebf5]">
          Treatment Summary
        </h3>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <Stethoscope className="w-4 h-4 text-[#704aaf]" />
            <span className="text-sm text-[#6b6b6b]">Treatment:</span>
            <span className="text-sm font-medium text-[#2d2d2d]">{treatment.treatment}</span>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-[#704aaf]" />
            <span className="text-sm text-[#6b6b6b]">Date & Time:</span>
            <span className="text-sm font-medium text-[#2d2d2d]">{treatment.dateTime}</span>
          </div>
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-[#704aaf]" />
            <span className="text-sm text-[#6b6b6b]">Practitioner:</span>
            <span className="text-sm font-medium text-[#2d2d2d]">{treatment.practitioner}</span>
          </div>
        </div>
      </div>

      {/* Pricing & Payments Card */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#6b6b6b]">Price:</span>
              <span className="text-2xl font-bold text-[#2d2d2d]">£{treatment.price.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onStartTreatment}
                className="px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
              >
                Start Treatment
              </button>
              <button
                onClick={onRebook}
                className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#3d3d3d] text-sm font-medium border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
              >
                <CalendarPlus className="w-4 h-4 text-[#704aaf]" />
                Rebook
                <ChevronDown className="w-4 h-4" />
              </button>
              <button
                onClick={onTakePayment}
                className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#3d3d3d] text-sm font-medium border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
              >
                <CreditCard className="w-4 h-4 text-[#704aaf]" />
                Take Payment
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[#6b6b6b]">Paid:</span>
            <span className="font-medium text-[#2d2d2d]">Paid £{treatment.paidAmount.toFixed(2)}</span>
            <span className="text-[#c4c4c4]">/</span>
            <span className="text-[#6b6b6b]">Balance:</span>
            <span className="font-medium text-[#2d2d2d]">£{balance.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Compliance Indicators */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#6b6b6b] w-20">Consent:</span>
            <ConsentBadge status={treatment.consent} />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#6b6b6b] w-20">Aftercare:</span>
            <span className="text-sm text-[#3d3d3d]">{treatment.aftercare}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#6b6b6b] w-20">Status:</span>
            <StatusBadge status={treatment.status} />
          </div>
        </div>
      </div>

      {/* Complete Treatment Button */}
      <div className="flex justify-end">
        <button
          onClick={onCompleteTreatment}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
        >
          <CheckCircle2 className="w-4 h-4" />
          Complete Treatment
        </button>
      </div>
    </div>
  );
};

// Notes Tab Content
const NotesTab = ({ treatment }: { treatment: TreatmentDetail }) => {
  const [clinicalNotes, setClinicalNotes] = useState(treatment.clinicalNotes);
  const [aftercareNotes, setAftercareNotes] = useState(treatment.aftercareNotes);
  const [shareWithPatient, setShareWithPatient] = useState(treatment.shareWithPatient);
  const [additionalNotes, setAdditionalNotes] = useState(treatment.additionalNotes);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [noteModalType, setNoteModalType] = useState<"clinical" | "aftercare" | "additional">("additional");

  const openAddNoteModal = (type: "clinical" | "aftercare" | "additional") => {
    setNoteModalType(type);
    setShowAddNoteModal(true);
  };

  const handleAddNote = (data: { content: string; type: string }) => {
    const newNote: Note = {
      id: Date.now().toString(),
      content: data.content,
      author: "Emma Taylor",
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      avatarBg: "bg-pink-100 text-pink-600",
    };
    setAdditionalNotes([newNote, ...additionalNotes]);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clinical Notes */}
        <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
            <h3 className="text-base font-semibold text-[#2d2d2d]">
              Clinical Notes <span className="text-[#6b6b6b] font-normal">(Optional)</span>
            </h3>
            <button
              onClick={() => openAddNoteModal("clinical")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Note
            </button>
          </div>
          <div className="p-5">
            <textarea
              value={clinicalNotes}
              onChange={(e) => setClinicalNotes(e.target.value)}
              placeholder="Write clinical notes..."
              className="w-full px-4 py-3 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-28 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2 text-sm text-[#6b6b6b]">
                <MapPin className="w-4 h-4" />
                <span>Practimetment</span>
              </div>
              <button className="px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors">
                Save Notes
              </button>
            </div>
          </div>
        </div>

        {/* Aftercare Notes */}
        <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
            <h3 className="text-base font-semibold text-[#2d2d2d]">
              Aftercare Notes <span className="text-[#6b6b6b] font-normal">(Optional)</span>
            </h3>
            <button
              onClick={() => openAddNoteModal("aftercare")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Note
            </button>
          </div>
          <div className="p-5">
            <textarea
              value={aftercareNotes}
              onChange={(e) => setAftercareNotes(e.target.value)}
              placeholder="Write aftercare instructions (patient-visible)"
              className="w-full px-4 py-3 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-28 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={shareWithPatient}
                  onChange={(e) => setShareWithPatient(e.target.checked)}
                  className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
                />
                <span className="text-sm text-[#5c5c5c]">Share with client via Patient Portal</span>
                <ToggleSwitch checked={shareWithPatient} onChange={setShareWithPatient} />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button className="px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors">
                Save Notes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Save Notes Button - Center */}
      <div className="flex justify-center">
        <button className="px-6 py-2.5 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors">
          Save Notes
        </button>
      </div>

      {/* Additional Notes */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <h3 className="text-base font-semibold text-[#2d2d2d]">
            Additional Notes <span className="text-[#6b6b6b] font-normal">(Add Note)</span>
          </h3>
          <button
            onClick={() => openAddNoteModal("additional")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Note
          </button>
        </div>
        <div className="p-5">
          {additionalNotes.length > 0 ? (
            <div className="space-y-4">
              {additionalNotes.map((note) => (
                <div key={note.id} className="border-b border-[#f0ebf5] pb-4 last:border-0 last:pb-0">
                  <p className="text-sm font-medium text-[#6b6b6b] mb-2">{note.date}</p>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${note.avatarBg}`}>
                      {note.author.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[#704aaf]">{note.author}</p>
                      <p className="text-sm text-[#5c5c5c] mt-1">{note.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#6b6b6b] text-center py-8">No additional notes yet</p>
          )}
        </div>
        <div className="px-5 py-4 border-t border-[#f0ebf5] flex justify-end">
          <button className="px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors">
            Save Notes
          </button>
        </div>
      </div>

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={showAddNoteModal}
        onClose={() => setShowAddNoteModal(false)}
        onSave={handleAddNote}
        noteType={noteModalType}
        title={
          noteModalType === "clinical"
            ? "Add Clinical Note"
            : noteModalType === "aftercare"
            ? "Add Aftercare Note"
            : "Add Note"
        }
      />
    </div>
  );
};

// Photos Tab Content
const PhotosTab = ({ treatment }: { treatment: TreatmentDetail }) => {
  const [activePhotoFilter, setActivePhotoFilter] = useState<PhotoFilterType>("before");
  const [showAddPhotosModal, setShowAddPhotosModal] = useState(false);

  const filteredPhotos = treatment.photos.filter((p) => p.type === activePhotoFilter);

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        {/* Before/After Toggle */}
        <div className="flex items-center gap-1 bg-white border border-[#e8e0f0] p-1 rounded-lg">
          <button
            onClick={() => setActivePhotoFilter("before")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activePhotoFilter === "before"
                ? "bg-[#f0ebf5] text-[#704aaf]"
                : "text-[#6b6b6b] hover:bg-[#f8f5fc]"
            }`}
          >
            Before
          </button>
          <button
            onClick={() => setActivePhotoFilter("after")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activePhotoFilter === "after"
                ? "bg-[#f0ebf5] text-[#704aaf]"
                : "text-[#6b6b6b] hover:bg-[#f8f5fc]"
            }`}
          >
            After
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <button className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors">
            <Grid3X3 className="w-4 h-4" />
            All Areas
            <ChevronDown className="w-4 h-4" />
          </button>
          <button className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors">
            {treatment.treatment}
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Section Title */}
      <h3 className="text-base font-semibold text-[#2d2d2d] capitalize">{activePhotoFilter}</h3>

      {/* Photo Grid */}
      {filteredPhotos.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredPhotos.map((photo) => (
              <div key={photo.id} className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden hover:border-[#704aaf] transition-colors">
                <div className="aspect-square bg-gray-100 relative">
                  <img src={photo.url} alt={`${photo.type} photo`} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <p className="font-medium text-[#2d2d2d] capitalize">{photo.type}</p>
                  <p className="text-sm text-[#6b6b6b]">{photo.date}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-[#9b9b9b]">By {photo.practitioner}</p>
                    <button className="p-1.5 text-[#9b9b9b] hover:text-[#704aaf] transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-[#6b6b6b]">
            <p>Photos inherit treatment-vel consent. To upload new photos please do so from the "Treatments" tab.</p>
            <div className="flex items-center gap-2">
              <span>1 – {filteredPhotos.length} of {filteredPhotos.length} treatments</span>
              <button className="p-1.5 hover:bg-[#f8f5fc] rounded transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button className="p-1.5 hover:bg-[#f8f5fc] rounded transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border border-[#e8e0f0] p-12 text-center">
          <Camera className="w-16 h-16 text-[#e8e0f0] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#2d2d2d] mb-2">No {activePhotoFilter} photos</h3>
          <p className="text-[#6b6b6b] mb-4">Add photos to document the treatment progress</p>
          <button
            onClick={() => setShowAddPhotosModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Photos
          </button>
        </div>
      )}

      {/* Add Photos Modal */}
      <AddPhotosModal
        isOpen={showAddPhotosModal}
        onClose={() => setShowAddPhotosModal(false)}
        onSave={(data) => console.log("Photos saved:", data)}
      />
    </div>
  );
};

// Documents Tab Content
const DocumentsTab = ({ treatment }: { treatment: TreatmentDetail }) => {
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  const handlePreviewDocument = (doc: Document) => {
    setSelectedDocument(doc);
    setShowPreviewModal(true);
  };

  const handleShareDocument = (doc: Document) => {
    setSelectedDocument(doc);
    setShowShareModal(true);
  };

  const handleShareFromPreview = () => {
    setShowPreviewModal(false);
    setShowShareModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#2d2d2d]">Documents</h3>
        <button
          onClick={() => setShowAddDocumentModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#6b6b6b] bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Document
        </button>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#f3eff8] border-b border-[#e8e0f0]">
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Document
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Category
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Status
              </th>
              <th className="text-left px-5 py-3.5 text-[13px] font-semibold text-[#6b5b7a] tracking-wide">
                Date Uploaded
              </th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {treatment.documents.length > 0 ? (
              treatment.documents.map((doc) => (
                <tr key={doc.id} className="border-b border-[#f0ebf5] hover:bg-[#faf8fc] transition-colors">
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => handlePreviewDocument(doc)}
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#f0ebf5] flex items-center justify-center group-hover:bg-[#e0d6f0] transition-colors">
                        <FileText className="w-4 h-4 text-[#704aaf]" />
                      </div>
                      <span className="text-[14px] font-medium text-[#704aaf] group-hover:underline">
                        {doc.name}
                      </span>
                    </button>
                  </td>
                  <td className="px-5 py-3.5 text-[14px] text-[#5c5c5c]">{doc.category}</td>
                  <td className="px-5 py-3.5">
                    <DocumentStatusBadge status={doc.status} />
                  </td>
                  <td className="px-5 py-3.5 text-[14px] text-[#5c5c5c]">{doc.dateUploaded}</td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => handleShareDocument(doc)}
                      className="p-2 text-[#9b9b9b] hover:text-[#704aaf] hover:bg-[#f0ebf5] rounded-lg transition-colors"
                      title="Share Document"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-[#6b6b6b]">
                  No documents uploaded yet
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {treatment.documents.length > 0 && (
          <div className="px-5 py-4 border-t border-[#f0ebf5] flex items-center justify-end gap-2 text-sm text-[#6b6b6b]">
            <span>1 – {treatment.documents.length} of {treatment.documents.length} documents</span>
            <button className="p-1.5 hover:bg-[#f8f5fc] rounded transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button className="p-1.5 hover:bg-[#f8f5fc] rounded transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Add Document Modal */}
      <AddDocumentModal
        isOpen={showAddDocumentModal}
        onClose={() => setShowAddDocumentModal(false)}
        onSave={(data) => console.log("Document saved:", data)}
      />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        onShare={handleShareFromPreview}
        document={selectedDocument || undefined}
      />

      {/* Share Document Modal */}
      <ShareDocumentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={(data) => console.log("Document shared:", data)}
        document={selectedDocument ? { name: selectedDocument.name, category: selectedDocument.category } : undefined}
        clientName={treatment.client.name}
      />
    </div>
  );
};

// Log Tab Content
const LogTab = ({ treatment }: { treatment: TreatmentDetail }) => {
  const getLogTypeIcon = (type: string) => {
    switch (type) {
      case "system":
        return "bg-gray-400";
      case "payment":
        return "bg-emerald-500";
      case "document":
        return "bg-blue-500";
      case "status":
        return "bg-amber-500";
      default:
        return "bg-[#704aaf]";
    }
  };

  const getLogTypeLabel = (type: string) => {
    switch (type) {
      case "system":
        return { bg: "bg-gray-100", text: "text-gray-600", label: "System" };
      case "payment":
        return { bg: "bg-emerald-50", text: "text-emerald-600", label: "Payment" };
      case "document":
        return { bg: "bg-blue-50", text: "text-blue-600", label: "Document" };
      case "status":
        return { bg: "bg-amber-50", text: "text-amber-600", label: "Status" };
      default:
        return { bg: "bg-purple-50", text: "text-[#704aaf]", label: "Activity" };
    }
  };

  // Sort logs by time (most recent first)
  const sortedLogs = [...treatment.logs].reverse();

  return (
    <div className="bg-white rounded-xl border border-[#e8e0f0] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
        <h3 className="text-base font-semibold text-[#2d2d2d]">
          Activity Log
        </h3>
        <span className="text-sm text-[#6b6b6b]">{treatment.logs.length} entries</span>
      </div>
      <div className="p-5">
        {sortedLogs.length > 0 ? (
          <div className="space-y-4">
            {sortedLogs.map((log) => {
              const typeStyle = getLogTypeLabel(log.type);
              return (
                <div key={log.id} className="flex items-start gap-3 pb-4 border-b border-[#f0ebf5] last:border-0 last:pb-0">
                  <div className={`w-2.5 h-2.5 mt-1.5 rounded-full ${getLogTypeIcon(log.type)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#2d2d2d]">{log.action}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${typeStyle.bg} ${typeStyle.text}`}>
                        {typeStyle.label}
                      </span>
                    </div>
                    <p className="text-sm text-[#6b6b6b] mt-0.5">{log.user} • {log.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-[#6b6b6b]">
            No activity logged yet
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
export default function AestheticTreatmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [showStartMenu, setShowStartMenu] = useState(false);
  const [showRebookMenu, setShowRebookMenu] = useState(false);
  const [showPaymentMenu, setShowPaymentMenu] = useState(false);
  const [showAddPhotosModal, setShowAddPhotosModal] = useState(false);
  const [showAddDocumentsModal, setShowAddDocumentsModal] = useState(false);
  const [showTakePaymentModal, setShowTakePaymentModal] = useState(false);
  const [showRebookModal, setShowRebookModal] = useState(false);
  const [showScheduleStartModal, setShowScheduleStartModal] = useState(false);

  // Get treatment data
  const treatment = staticTreatmentData[id || "1"] || getDefaultTreatment(id || "1");

  const handleStartTreatment = () => {
    console.log("Starting treatment...");
    setShowStartMenu(false);
  };

  const handleScheduleStart = () => {
    setShowScheduleStartModal(true);
    setShowStartMenu(false);
  };

  const handleCompleteTreatment = () => {
    console.log("Completing treatment...");
  };

  const handleRebook = () => {
    setShowRebookModal(true);
    setShowRebookMenu(false);
  };

  const handleTakePayment = () => {
    setShowTakePaymentModal(true);
    setShowPaymentMenu(false);
  };

  const handleCalendarClick = () => {
    navigate("/calendar");
  };

  const handlePaymentSubmit = (data: { amount: number; method: string; notes: string }) => {
    console.log("Payment submitted:", data);
    // Here you would call your API to process the payment
  };

  const handleRebookSubmit = (data: any) => {
    console.log("Rebook submitted:", data);
    // Here you would call your API to create the follow-up appointment
  };

  const handleScheduleStartSubmit = (data: { date: Date; time: string; sendReminder: boolean }) => {
    console.log("Schedule start submitted:", data);
    // Here you would call your API to schedule the treatment start
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "notes", label: "Notes" },
    { id: "photos", label: "Photos" },
    { id: "documents", label: "Documents" },
    { id: "log", label: "Log" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <OverviewTab
            treatment={treatment}
            onStartTreatment={handleStartTreatment}
            onCompleteTreatment={handleCompleteTreatment}
            onRebook={handleRebook}
            onTakePayment={handleTakePayment}
          />
        );
      case "notes":
        return <NotesTab treatment={treatment} />;
      case "photos":
        return <PhotosTab treatment={treatment} />;
      case "documents":
        return <DocumentsTab treatment={treatment} />;
      case "log":
        return <LogTab treatment={treatment} />;
      default:
        return null;
    }
  };

  // Get header action button based on tab
  const getHeaderAction = () => {
    if (activeTab === "photos") {
      return (
        <button
          onClick={() => setShowAddPhotosModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
        >
          Add Photos
          <ChevronDown className="w-4 h-4" />
        </button>
      );
    }
    if (activeTab === "documents") {
      return (
        <button
          onClick={() => setShowAddDocumentsModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
        >
          Add Documents
          <ChevronDown className="w-4 h-4" />
        </button>
      );
    }
    return (
      <div className="flex items-center gap-2">
        {/* Start Treatment Dropdown */}
        <div className="relative">
          <button
            onClick={() => {
              setShowStartMenu(!showStartMenu);
              setShowRebookMenu(false);
              setShowPaymentMenu(false);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors"
          >
            <Play className="w-4 h-4" />
            Start Treatment
            <ChevronDown className="w-4 h-4" />
          </button>
          {showStartMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-[#e8e0f0] py-1 z-10">
              <button
                onClick={handleStartTreatment}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors flex items-center gap-2"
              >
                <Play className="w-4 h-4 text-[#704aaf]" />
                Start Now
              </button>
              <button
                onClick={handleScheduleStart}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors flex items-center gap-2"
              >
                <Calendar className="w-4 h-4 text-[#704aaf]" />
                Schedule Start
              </button>
            </div>
          )}
        </div>

        {/* Rebook Dropdown */}
        <div className="relative">
          <button
            onClick={() => {
              setShowRebookMenu(!showRebookMenu);
              setShowStartMenu(false);
              setShowPaymentMenu(false);
            }}
            className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#3d3d3d] text-sm font-medium border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
          >
            <CalendarPlus className="w-4 h-4 text-[#704aaf]" />
            Rebook
            <ChevronDown className="w-4 h-4" />
          </button>
          {showRebookMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-[#e8e0f0] py-1 z-10">
              <button
                onClick={handleRebook}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors flex items-center gap-2"
              >
                <CalendarPlus className="w-4 h-4 text-[#704aaf]" />
                Book Follow-up
              </button>
              <button
                onClick={() => {
                  navigate("/calendar");
                  setShowRebookMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors flex items-center gap-2"
              >
                <Calendar className="w-4 h-4 text-[#704aaf]" />
                View Calendar
              </button>
            </div>
          )}
        </div>

        {/* Take Payment Dropdown */}
        <div className="relative">
          <button
            onClick={() => {
              setShowPaymentMenu(!showPaymentMenu);
              setShowStartMenu(false);
              setShowRebookMenu(false);
            }}
            className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#3d3d3d] text-sm font-medium border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
          >
            <CreditCard className="w-4 h-4 text-[#704aaf]" />
            Take Payment
            <ChevronDown className="w-4 h-4" />
          </button>
          {showPaymentMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-[#e8e0f0] py-1 z-10">
              <button
                onClick={handleTakePayment}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4 text-[#0d7a3e]" />
                Take Payment
              </button>
              <button
                onClick={() => {
                  console.log("View payment history");
                  setShowPaymentMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-[#3d3d3d] hover:bg-[#f8f5fc] transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4 text-[#704aaf]" />
                View History
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-[#f8f5fc]">
      {/* Back Button */}
      <div className="px-6 pt-4">
        <button
          onClick={() => navigate("/aesthetics/treatments")}
          className="flex items-center gap-1 text-[#764BB7] hover:text-[#7a5a9e] text-sm font-medium transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Treatments
        </button>
      </div>

      {/* Header */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left - Profile Info */}
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-semibold border-2 border-white shadow-md ${treatment.client.avatarBg}`}>
              {treatment.client.initials}
            </div>

            {/* Name & Details */}
            <div>
              <h1 className="text-xl font-bold text-[#2d2d2d]">{treatment.client.name}</h1>
              <p className="text-sm text-[#704aaf] font-medium">{treatment.treatment}</p>
              <p className="text-xs text-[#6b6b6b]">{treatment.dateTime}</p>
            </div>
          </div>

          {/* Right - Actions */}
          <div className="flex items-center gap-2">
            {getHeaderAction()}
            <button
              onClick={handleCalendarClick}
              className="p-2 bg-white border border-[#e8e0f0] rounded-lg hover:bg-[#f8f5fc] transition-colors"
              title="View Calendar"
            >
              <Calendar className="w-5 h-5 text-[#6b6b6b]" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-[#e8e0f0] bg-white">
        <div className="flex gap-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-[#764BB7] text-[#764BB7]"
                  : "border-transparent text-[#6b6b6b] hover:text-[#764BB7]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">{renderTabContent()}</div>

      {/* Click outside to close dropdowns */}
      {(showStartMenu || showRebookMenu || showPaymentMenu) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowStartMenu(false);
            setShowRebookMenu(false);
            setShowPaymentMenu(false);
          }}
        />
      )}

      {/* Global Modals */}
      <AddPhotosModal
        isOpen={showAddPhotosModal}
        onClose={() => setShowAddPhotosModal(false)}
        onSave={(data) => console.log("Photos saved:", data)}
      />
      <AddDocumentModal
        isOpen={showAddDocumentsModal}
        onClose={() => setShowAddDocumentsModal(false)}
        onSave={(data) => console.log("Document saved:", data)}
      />
      <TakePaymentModal
        isOpen={showTakePaymentModal}
        onClose={() => setShowTakePaymentModal(false)}
        onPayment={handlePaymentSubmit}
        treatment={{
          clientName: treatment.client.name,
          treatmentName: treatment.treatment,
          totalPrice: treatment.price,
          paidAmount: treatment.paidAmount,
        }}
      />
      <RebookTreatmentModal
        isOpen={showRebookModal}
        onClose={() => setShowRebookModal(false)}
        onSave={handleRebookSubmit}
        treatment={{
          clientName: treatment.client.name,
          treatmentName: treatment.treatment,
          practitioner: treatment.practitioner,
        }}
      />
      <ScheduleStartModal
        isOpen={showScheduleStartModal}
        onClose={() => setShowScheduleStartModal(false)}
        onSchedule={handleScheduleStartSubmit}
        treatment={{
          clientName: treatment.client.name,
          treatmentName: treatment.treatment,
          scheduledDate: treatment.dateTime.split(" at ")[0],
          scheduledTime: treatment.dateTime.split(" at ")[1] || "10:00 AM",
        }}
      />
    </div>
  );
}